// src/services/notificationService.ts - Service pour gérer les notifications de stock

import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  updateDoc, 
  doc,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { 
  Notification, 
  NotificationType, 
  STOCK_NOTIFICATION_MESSAGES,
  BIRTHDAY_NOTIFICATION_MESSAGES,
  APPOINTMENT_NOTIFICATION_MESSAGES,
  SUPPORT_TICKET_NOTIFICATION_MESSAGES,
  NotificationSummary 
} from '../types/notifications';

/**
 * Obtenir la priorité d'une notification (plus petite valeur = plus haute priorité)
 */
function getNotificationPriority(type: NotificationType): number {
  switch (type) {
    // CRITIQUE (priorité 1) - Action immédiate requise
    case 'out_of_stock':
      return 1;
    
    // URGENT (priorité 2) - Action requise dans les prochaines heures
    case 'appointment_reminder_2h':
      return 2;
    
    // IMPORTANT (priorité 3) - Action requise dans les prochaines 24h
    case 'low_stock':
    case 'appointment_reminder_24h':
      return 3;

    // Paiement en plusieurs — échéance proche
    case 'payment_plan_reminder_due':
    case 'payment_plan_overdue':
      return 2;
    case 'payment_plan_reminder_1d':
      return 3;
    case 'payment_plan_reminder_2d':
      return 4;
    case 'activity_report_submit_reminder':
      return 2;
    case 'activity_report_overdue':
      return 2;
    case 'activity_report_validated':
      return 3;
    case 'licence_payment_blocked':
      return 1;
    case 'licence_payment_due_1d':
      return 2;
    case 'licence_payment_due_7d':
      return 3;
    case 'messages_clients_ready':
    case 'messages_clients_no_phone':
      return 3;
    case 'manufacturing_order_on_hold':
    case 'manufacturing_order_done':
      return 2;
    case 'inventory_audit_started':
    case 'inventory_audit_to_close':
      return 2;
    case 'inventory_loss_created':
      return 3;
    case 'performance_sheets_generated':
      return 3;
    case 'performance_sheet_validated':
      return 3;
    
    // INFO (priorité 4) - Information utile mais pas urgente
    case 'birthday':
    case 'inactive_client':
    case 'loyalty_milestone':
    case 'support_ticket_reply':
    case 'support_ticket_resolved':
    case 'support_ticket_opened_for_you':
      return 4;

    case 'support_ticket_created':
      return 3;
    
    default:
      return 5; // Priorité par défaut
  }
}

class NotificationService {
  private static readonly COLLECTION_NAME = COLLECTIONS.notifications;

  /**
   * Créer une notification de stock
   */
  static async createStockNotification(data: {
    businessId: string;
    productId: string;
    productName: string;
    currentStock: number;
    minStockAlert: number;
    siteId?: string;
    siteName?: string;
    triggeredBy?: string;
    triggeredByUser?: string;
  }): Promise<string> {
    const {
      businessId,
      productId,
      productName,
      currentStock,
      minStockAlert,
      siteId,
      siteName,
      triggeredBy,
      triggeredByUser,
    } = data;

    // Si le stock repasse au-dessus du seuil, on clôture les alertes actives du scope concerné.
    if (currentStock > minStockAlert) {
      await this.resolveStockNotificationsForScope(businessId, productId, siteId);
      return '';
    }

    // Déterminer le type et la sévérité
    const type: NotificationType = currentStock === 0 ? 'out_of_stock' : 'low_stock';
    const severity = currentStock === 0 ? 'critical' : 'warning';
    
    // Générer le message
    const message = type === 'out_of_stock' 
      ? STOCK_NOTIFICATION_MESSAGES.out_of_stock(productName)
      : STOCK_NOTIFICATION_MESSAGES.low_stock(productName, currentStock);

    // Vérifier si une notification similaire existe déjà (éviter les doublons)
    const existingNotification = await this.findExistingStockNotification(businessId, productId, type, siteId);
    if (existingNotification) {
      // Mettre à jour la notification existante
      await updateDoc(doc(db, this.COLLECTION_NAME, existingNotification.id), {
        currentStock,
        message,
        timestamp: Timestamp.now(),
        read: false, // Marquer comme non lu pour attirer l'attention
        ...(siteId ? { siteId } : {}),
        ...(siteName ? { siteName } : {}),
      });
      await this.resolveOppositeStockTypeNotification(businessId, productId, type, siteId);
      return existingNotification.id;
    }

    // Créer une nouvelle notification
    const notificationData = {
      type,
      severity,
      businessId,
      productId,
      productName,
      currentStock,
      minStockAlert,
      ...(siteId ? { siteId } : {}),
      ...(siteName ? { siteName } : {}),
      message,
      timestamp: Timestamp.now(),
      read: false,
      ...(triggeredBy && { triggeredBy }),
      ...(triggeredByUser && { triggeredByUser }),
    };

    const docRef = await addDoc(collection(db, this.COLLECTION_NAME), notificationData);
    await this.resolveOppositeStockTypeNotification(businessId, productId, type, siteId);
    return docRef.id;
  }

  /**
   * Créer une notification de rappel de rendez-vous
   */
  static async createAppointmentNotification(data: {
    businessId: string;
    appointmentId: string;
    clientId: string;
    clientName: string;
    appointmentTitle: string;
    appointmentDate: Date;
    appointmentTime: string;
    employeeName?: string;
    reminderType: '24h' | '2h';
    triggeredBy?: string;
    triggeredByUser?: string;
  }): Promise<string> {
    const { 
      businessId, 
      appointmentId, 
      clientId, 
      clientName, 
      appointmentTitle, 
      appointmentDate, 
      appointmentTime, 
      employeeName,
      reminderType,
      triggeredBy, 
      triggeredByUser 
    } = data;
    
    const type: NotificationType = reminderType === '24h' ? 'appointment_reminder_24h' : 'appointment_reminder_2h';
    const severity = 'info';
    const message = reminderType === '24h' 
      ? APPOINTMENT_NOTIFICATION_MESSAGES.appointment_reminder_24h(clientName, appointmentTitle, appointmentTime)
      : APPOINTMENT_NOTIFICATION_MESSAGES.appointment_reminder_2h(clientName, appointmentTitle, appointmentTime);

    // Vérifier s'il existe déjà une notification de rappel pour ce rendez-vous
    const existingNotification = await this.findExistingAppointmentNotification(businessId, appointmentId, type);
    if (existingNotification) {
      await updateDoc(doc(db, this.COLLECTION_NAME, existingNotification.id), {
        message,
        timestamp: Timestamp.now(),
        read: false,
      });
      return existingNotification.id;
    }

    const notificationData = {
      type,
      severity,
      businessId,
      appointmentId,
      clientId,
      clientName,
      appointmentDate: Timestamp.fromDate(appointmentDate),
      appointmentTime,
      appointmentTitle,
      employeeName,
      message,
      timestamp: Timestamp.now(),
      read: false,
      ...(triggeredBy && { triggeredBy }),
      ...(triggeredByUser && { triggeredByUser }),
    };

    const docRef = await addDoc(collection(db, this.COLLECTION_NAME), notificationData);
    return docRef.id;
  }

  /**
   * Créer une notification de rappel d'échéance (paiement en plusieurs).
   */
  static async createPaymentPlanReminderNotification(data: {
    businessId: string;
    paymentPlanId: string;
    clientId: string;
    clientName: string;
    targetDate: Date;
    amountRemaining: number;
    saleReceiptNumber?: string;
    reminderType: NotificationType;
    message: string;
    severity: 'info' | 'warning' | 'critical';
    triggeredBy?: string;
    triggeredByUser?: string;
  }): Promise<string | null> {
    const {
      businessId,
      paymentPlanId,
      clientId,
      clientName,
      targetDate,
      amountRemaining,
      saleReceiptNumber,
      reminderType,
      message,
      severity,
      triggeredBy,
      triggeredByUser,
    } = data;

    const existingNotification = await this.findExistingPaymentPlanReminderNotification(
      businessId,
      paymentPlanId,
      reminderType,
      targetDate
    );
    if (existingNotification) {
      await updateDoc(doc(db, this.COLLECTION_NAME, existingNotification.id), {
        message,
        amountRemaining,
        timestamp: Timestamp.now(),
        read: false,
      });
      return existingNotification.id;
    }

    const notificationData = {
      type: reminderType,
      severity,
      businessId,
      paymentPlanId,
      clientId,
      clientName,
      targetDate: Timestamp.fromDate(targetDate),
      amountRemaining,
      ...(saleReceiptNumber ? { saleReceiptNumber } : {}),
      message,
      timestamp: Timestamp.now(),
      read: false,
      ...(triggeredBy && { triggeredBy }),
      ...(triggeredByUser && { triggeredByUser }),
    };

    const docRef = await addDoc(collection(db, this.COLLECTION_NAME), notificationData);
    return docRef.id;
  }

  /**
   * Créer une notification d'anniversaire
   */
  static async createBirthdayNotification(data: {
    businessId: string;
    clientId: string;
    clientName: string;
    triggeredBy?: string;
    triggeredByUser?: string;
  }): Promise<string> {
    const { businessId, clientId, clientName, triggeredBy, triggeredByUser } = data;
    
    const type: NotificationType = 'birthday';
    const severity = 'info';
    const message = BIRTHDAY_NOTIFICATION_MESSAGES.birthday(clientName);

    // Vérifier s'il existe déjà une notification d'anniversaire pour ce client aujourd'hui
    const existingNotification = await this.findExistingBirthdayNotification(businessId, clientId);
    if (existingNotification) {
      // Mettre à jour la notification existante
      await updateDoc(doc(db, this.COLLECTION_NAME, existingNotification.id), {
        message,
        timestamp: Timestamp.now(),
        read: false,
      });
      return existingNotification.id;
    }

    const notificationData = {
      type,
      severity,
      businessId,
      clientId,
      clientName,
      message,
      timestamp: Timestamp.now(),
      read: false,
      ...(triggeredBy && { triggeredBy }),
      ...(triggeredByUser && { triggeredByUser }),
    };

    const docRef = await addDoc(collection(db, this.COLLECTION_NAME), notificationData);
    return docRef.id;
  }

  /** Nouveau ticket → SA / CSM du business */
  static async createSupportTicketCreatedNotification(data: {
    businessId: string;
    supportTicketId: string;
    subject: string;
    authorName: string;
    triggeredBy?: string;
  }): Promise<string> {
    const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
      type: 'support_ticket_created' as NotificationType,
      severity: 'info',
      businessId: data.businessId,
      audience: 'nauvatek_staff',
      supportTicketId: data.supportTicketId,
      supportTicketSubject: data.subject,
      message: SUPPORT_TICKET_NOTIFICATION_MESSAGES.support_ticket_created(
        data.authorName,
        data.subject
      ),
      timestamp: Timestamp.now(),
      read: false,
      ...(data.triggeredBy
        ? { triggeredBy: data.triggeredBy, triggeredByUser: data.authorName }
        : {}),
    });
    return docRef.id;
  }

  /** Réponse NauvaTek → auteur du ticket */
  static async createSupportTicketReplyNotification(data: {
    businessId: string;
    supportTicketId: string;
    subject: string;
    recipientUserId: string;
    triggeredBy?: string;
    triggeredByUser?: string;
  }): Promise<string> {
    const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
      type: 'support_ticket_reply' as NotificationType,
      severity: 'info',
      businessId: data.businessId,
      audience: 'user',
      recipientUserId: data.recipientUserId,
      supportTicketId: data.supportTicketId,
      supportTicketSubject: data.subject,
      message: SUPPORT_TICKET_NOTIFICATION_MESSAGES.support_ticket_reply(data.subject),
      timestamp: Timestamp.now(),
      read: false,
      ...(data.triggeredBy ? { triggeredBy: data.triggeredBy } : {}),
      ...(data.triggeredByUser ? { triggeredByUser: data.triggeredByUser } : {}),
    });
    return docRef.id;
  }

  /** Ticket résolu → auteur du ticket */
  static async createSupportTicketResolvedNotification(data: {
    businessId: string;
    supportTicketId: string;
    subject: string;
    recipientUserId: string;
    triggeredBy?: string;
    triggeredByUser?: string;
  }): Promise<string> {
    const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
      type: 'support_ticket_resolved' as NotificationType,
      severity: 'info',
      businessId: data.businessId,
      audience: 'user',
      recipientUserId: data.recipientUserId,
      supportTicketId: data.supportTicketId,
      supportTicketSubject: data.subject,
      message: SUPPORT_TICKET_NOTIFICATION_MESSAGES.support_ticket_resolved(data.subject),
      timestamp: Timestamp.now(),
      read: false,
      ...(data.triggeredBy ? { triggeredBy: data.triggeredBy } : {}),
      ...(data.triggeredByUser ? { triggeredByUser: data.triggeredByUser } : {}),
    });
    return docRef.id;
  }

  /** Ticket ouvert par NauvaTek pour un employé */
  static async createSupportTicketOpenedForYouNotification(data: {
    businessId: string;
    supportTicketId: string;
    subject: string;
    recipientUserId: string;
    triggeredBy?: string;
    triggeredByUser?: string;
  }): Promise<string> {
    const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
      type: 'support_ticket_opened_for_you' as NotificationType,
      severity: 'info',
      businessId: data.businessId,
      audience: 'user',
      recipientUserId: data.recipientUserId,
      supportTicketId: data.supportTicketId,
      supportTicketSubject: data.subject,
      message: SUPPORT_TICKET_NOTIFICATION_MESSAGES.support_ticket_opened_for_you(data.subject),
      timestamp: Timestamp.now(),
      read: false,
      ...(data.triggeredBy ? { triggeredBy: data.triggeredBy } : {}),
      ...(data.triggeredByUser ? { triggeredByUser: data.triggeredByUser } : {}),
    });
    return docRef.id;
  }

  /**
   * Récupérer les notifications d'un business
   */
  static async getNotifications(businessId: string, options: {
    unreadOnly?: boolean;
    limit?: number;
    type?: NotificationType;
  } = {}): Promise<Notification[]> {
    const { unreadOnly = false, limit, type } = options;
    
    
    let q = query(
      collection(db, this.COLLECTION_NAME),
      where('businessId', '==', businessId),
      orderBy('timestamp', 'desc')
    );

    // Filtrer par type si spécifié
    if (type) {
      q = query(q, where('type', '==', type));
    }

    // Filtrer par statut de lecture si spécifié
    if (unreadOnly) {
      q = query(q, where('read', '==', false));
    }

    const snapshot = await getDocs(q);
    
    let notifications = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp.toDate(),
    })) as Notification[];

    // Trier les notifications par priorité intelligente
    notifications.sort((a, b) => {
      // 1. Priorité par type (critique > urgent > info)
      const priorityA = getNotificationPriority(a.type);
      const priorityB = getNotificationPriority(b.type);
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB; // Plus petite valeur = plus haute priorité
      }
      
      // 2. Si même priorité, trier par date de RDV pour les notifications de RDV
      if (a.appointmentDate && b.appointmentDate) {
        const dateA = a.appointmentDate instanceof Date ? a.appointmentDate : (a.appointmentDate as any).toDate();
        const dateB = b.appointmentDate instanceof Date ? b.appointmentDate : (b.appointmentDate as any).toDate();
        
        const dateComparison = dateA.getTime() - dateB.getTime();
        if (dateComparison !== 0) {
          return dateComparison; // Plus proche en premier
        }
      }
      
      // 3. Sinon, trier par timestamp (plus récent en premier)
      return b.timestamp.getTime() - a.timestamp.getTime();
    });

    // Limiter les résultats si spécifié
    if (limit) {
      notifications = notifications.slice(0, limit);
    }

    return notifications;
  }

  /**
   * Marquer une notification comme lue
   */
  static async markAsRead(notificationId: string): Promise<void> {
    await updateDoc(doc(db, this.COLLECTION_NAME, notificationId), {
      read: true,
    });
  }

  /**
   * Marquer toutes les notifications comme lues
   */
  static async markAllAsRead(businessId: string): Promise<void> {
    const notifications = await this.getNotifications(businessId, { unreadOnly: true });
    
    if (notifications.length === 0) return;

    const batch = writeBatch(db);
    notifications.forEach(notification => {
      const notificationRef = doc(db, this.COLLECTION_NAME, notification.id);
      batch.update(notificationRef, { read: true });
    });

    await batch.commit();
  }

  /**
   * Supprimer une notification
   */
  static async deleteNotification(notificationId: string): Promise<void> {
    await updateDoc(doc(db, this.COLLECTION_NAME, notificationId), {
      deleted: true,
      deletedAt: Timestamp.now(),
    });
  }

  /**
   * Obtenir un résumé des notifications
   */
  static async getNotificationSummary(businessId: string): Promise<NotificationSummary> {
    const notifications = await this.getNotifications(businessId);
    
    const summary: NotificationSummary = {
      total: notifications.length,
      unread: notifications.filter(n => !n.read).length,
    byType: {
      low_stock: notifications.filter(n => n.type === 'low_stock').length,
      out_of_stock: notifications.filter(n => n.type === 'out_of_stock').length,
      appointment_reminder_24h: notifications.filter(n => n.type === 'appointment_reminder_24h').length,
      appointment_reminder_2h: notifications.filter(n => n.type === 'appointment_reminder_2h').length,
      appointment_request: notifications.filter(n => n.type === 'appointment_request').length,
      pickup_request: notifications.filter(n => n.type === 'pickup_request').length,
      support_ticket_created: notifications.filter(n => n.type === 'support_ticket_created').length,
      support_ticket_reply: notifications.filter(n => n.type === 'support_ticket_reply').length,
      support_ticket_resolved: notifications.filter(n => n.type === 'support_ticket_resolved').length,
      support_ticket_opened_for_you: notifications.filter(n => n.type === 'support_ticket_opened_for_you').length,
      birthday: notifications.filter(n => n.type === 'birthday').length,
      inactive_client: notifications.filter(n => n.type === 'inactive_client').length,
      loyalty_milestone: notifications.filter(n => n.type === 'loyalty_milestone').length,
      payment_plan_reminder_2d: notifications.filter(n => n.type === 'payment_plan_reminder_2d').length,
      payment_plan_reminder_1d: notifications.filter(n => n.type === 'payment_plan_reminder_1d').length,
      payment_plan_reminder_due: notifications.filter(n => n.type === 'payment_plan_reminder_due').length,
      activity_report_submit_reminder: notifications.filter(n => n.type === 'activity_report_submit_reminder').length,
      activity_report_validated: notifications.filter(n => n.type === 'activity_report_validated').length,
      activity_report_overdue: notifications.filter(n => n.type === 'activity_report_overdue').length,
      payment_plan_overdue: notifications.filter(n => n.type === 'payment_plan_overdue').length,
      licence_payment_due_7d: notifications.filter(n => n.type === 'licence_payment_due_7d').length,
      licence_payment_due_1d: notifications.filter(n => n.type === 'licence_payment_due_1d').length,
      licence_payment_blocked: notifications.filter(n => n.type === 'licence_payment_blocked').length,
      messages_clients_ready: notifications.filter(n => n.type === 'messages_clients_ready').length,
      messages_clients_no_phone: notifications.filter(n => n.type === 'messages_clients_no_phone').length,
      manufacturing_order_on_hold: notifications.filter(n => n.type === 'manufacturing_order_on_hold').length,
      manufacturing_order_done: notifications.filter(n => n.type === 'manufacturing_order_done').length,
      inventory_audit_started: notifications.filter(n => n.type === 'inventory_audit_started').length,
      inventory_audit_to_close: notifications.filter(n => n.type === 'inventory_audit_to_close').length,
      inventory_loss_created: notifications.filter(n => n.type === 'inventory_loss_created').length,
      performance_sheets_generated: notifications.filter(n => n.type === 'performance_sheets_generated').length,
      performance_sheet_validated: notifications.filter(n => n.type === 'performance_sheet_validated').length,
    },
    bySeverity: {
      warning: notifications.filter(n => n.severity === 'warning').length,
      critical: notifications.filter(n => n.severity === 'critical').length,
      info: notifications.filter(n => n.severity === 'info').length,
    },
    };

    return summary;
  }

  /**
   * Vérifier si une notification de stock similaire existe déjà
   */
  private static async findExistingStockNotification(
    businessId: string, 
    productId: string, 
    type: NotificationType,
    siteId?: string
  ): Promise<Notification | null> {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('businessId', '==', businessId),
      where('productId', '==', productId),
      where('type', '==', type),
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const matchedDoc = snapshot.docs.find((d) => {
      const data = d.data();
      return this.isSameSiteScope(data.siteId, siteId);
    });
    if (!matchedDoc) return null;

    return {
      id: matchedDoc.id,
      ...matchedDoc.data(),
      timestamp: matchedDoc.data().timestamp.toDate(),
    } as Notification;
  }

  private static async resolveStockNotificationsForScope(
    businessId: string,
    productId: string,
    siteId?: string
  ): Promise<void> {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('businessId', '==', businessId),
      where('productId', '==', productId),
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    const toResolve = snapshot.docs.filter((d) => {
      const data = d.data();
      return (
        (data.type === 'low_stock' || data.type === 'out_of_stock') &&
        this.isSameSiteScope(data.siteId, siteId)
      );
    });

    if (toResolve.length === 0) return;

    const batch = writeBatch(db);
    toResolve.forEach((d) => {
      batch.update(d.ref, {
        read: true,
        resolvedAt: Timestamp.now(),
      });
    });
    await batch.commit();
  }

  private static async resolveOppositeStockTypeNotification(
    businessId: string,
    productId: string,
    currentType: NotificationType,
    siteId?: string
  ): Promise<void> {
    const oppositeType: NotificationType =
      currentType === 'out_of_stock' ? 'low_stock' : 'out_of_stock';

    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('businessId', '==', businessId),
      where('productId', '==', productId),
      where('type', '==', oppositeType),
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    const toResolve = snapshot.docs.filter((d) => this.isSameSiteScope(d.data().siteId, siteId));
    if (toResolve.length === 0) return;

    const batch = writeBatch(db);
    toResolve.forEach((d) => {
      batch.update(d.ref, {
        read: true,
        resolvedAt: Timestamp.now(),
      });
    });
    await batch.commit();
  }

  private static isSameSiteScope(notificationSiteId: string | undefined, targetSiteId?: string): boolean {
    if (targetSiteId) {
      // Compatibilité historique: les anciennes alertes sans site sont aussi rattachées
      // au scope du lieu lorsqu'on migre vers le modèle multi-lieux.
      return notificationSiteId === targetSiteId || !notificationSiteId;
    }
    return !notificationSiteId;
  }

  private static async findExistingAppointmentNotification(
    businessId: string,
    appointmentId: string,
    type: NotificationType
  ): Promise<Notification | null> {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('businessId', '==', businessId),
      where('appointmentId', '==', appointmentId),
      where('type', '==', type),
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp.toDate(),
      appointmentDate: doc.data().appointmentDate?.toDate(),
    } as Notification;
  }

  private static async findExistingPaymentPlanReminderNotification(
    businessId: string,
    paymentPlanId: string,
    type: NotificationType,
    targetDate: Date
  ): Promise<Notification | null> {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('businessId', '==', businessId),
      where('paymentPlanId', '==', paymentPlanId),
      where('type', '==', type)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const targetDay = new Date(targetDate);
    targetDay.setHours(0, 0, 0, 0);
    const targetDayMs = targetDay.getTime();

    const matched = snapshot.docs.find((docSnap) => {
      const raw = docSnap.data().targetDate?.toDate?.();
      if (!raw) return false;
      const day = new Date(raw);
      day.setHours(0, 0, 0, 0);
      return day.getTime() === targetDayMs;
    });

    if (!matched) return null;

    return {
      id: matched.id,
      ...matched.data(),
      timestamp: matched.data().timestamp.toDate(),
      targetDate: matched.data().targetDate?.toDate(),
    } as Notification;
  }

  /**
   * Trouver une notification d'anniversaire existante pour un client aujourd'hui
   */
  private static async findExistingBirthdayNotification(
    businessId: string, 
    clientId: string
  ): Promise<Notification | null> {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);

    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('businessId', '==', businessId),
      where('clientId', '==', clientId),
      where('type', '==', 'birthday'),
      where('timestamp', '>=', Timestamp.fromDate(startOfDay)),
      where('timestamp', '<', Timestamp.fromDate(endOfDay))
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp.toDate(),
    } as Notification;
  }

  /**
   * Nettoyer les anciennes notifications (plus de 30 jours)
   */
  static async cleanupOldNotifications(businessId: string): Promise<void> {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('businessId', '==', businessId),
      where('timestamp', '<', Timestamp.fromDate(thirtyDaysAgo))
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => {
      batch.update(doc.ref, { 
        deleted: true, 
        deletedAt: Timestamp.now() 
      });
    });

    await batch.commit();
  }
}

export default NotificationService;
